import { Trans } from '@lingui/react';
import { Dialog, DialogContent, DialogActions, DialogTitle } from '@startuptools/ui/Dialog';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GenericError } from '@startuptools/ui/Error';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Typography } from '@startuptools/ui/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useCompanySetSharesAtFoundationMutation } from '../../.././../graphql/react-operations';
import { TransactionsViewDocument } from '../transactions-view.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';

type FormData = {
  quantity: string;
};
export const EditSharesAtFoundation = ({ close, onSave }: { close: () => void; onSave: () => void }) => {
  const companyId = useCompanyId();
  const [setShares, { loading, error }] = useCompanySetSharesAtFoundationMutation({
    refetchQueries: [TransactionsViewDocument],
  });
  const save = async (data: FormData) => {
    await setShares({ variables: { companyId, input: { sharesAtFoundation: data.quantity } } });
    onSave();
  };
  const { control, handleSubmit } = useForm<FormData>({});
  return (
    <Dialog open>
      <DialogTitle>
        <Trans id="Set number of shares at foundation" />
      </DialogTitle>
      <form onSubmit={handleSubmit(save)}>
        <DialogContent>
          <Typography>
            <Trans id="Bolagsverket is missing data about the founding of this company. You need to enter some details yourself before we can set up the share register." />
          </Typography>
          <FormControl>
            <FormLabel>
              <Trans id="Number of shares at foundation" />
            </FormLabel>
            <Controller
              control={control}
              name="quantity"
              rules={{ required: true, min: 1 }}
              render={({ field, formState }) => (
                <FormControl error={Boolean(formState.errors.quantity)}>
                  <NumericFormatInput {...field} />
                  {Boolean(formState.errors.quantity) && (
                    <FormHelperText>
                      <InfoOutlinedIcon />
                      {formState.errors.quantity?.type === 'min' && (
                        <Trans id="The company should have at least 1 share" />
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </FormControl>
          {error && <GenericError />}
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={close} disabled={loading}>
            <Trans id="Cancel" />
          </Button>
          <Button type="submit" loading={loading}>
            <Trans id="Confirm" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
